import { useMemo } from 'react';

/**
 * Custom hook to format a cryptocurrency address by highlighting specific parts of it.
 *
 * @param {string} address - The cryptocurrency address to be formatted.
 * @returns {string} - The formatted address with specific parts highlighted.
 *
 * Example usage:
 * const formattedAddress = useFormattedCryptoAddress('0x1234567890abcdef1234567890abcdef');
 * // Returns: "<strong>0x12</strong>3456<strong>7890</strong>abcdef<strong>cdef</strong>"
 */
function useFormattedCryptoAddress(address: string): string {
  // useMemo is used to memoize the formatted address, recalculating only when the address changes.
  return useMemo(() => {
    // If the address is null, undefined, or shorter than 12 characters, return it as is.
    if (!address || address?.length < 12) {
      return address;
    }

    // Extract the first four characters of the address.
    const firstFour = address.substring(0, 4);
    // Extract the last four characters of the address.
    const lastFour = address.substring(address.length - 4);

    // Calculate the middle index of the address.
    const middleIndex = Math.floor(address.length / 2);
    // Extract four characters from the middle of the address.
    const middleFour = address.substring(middleIndex - 2, middleIndex + 2);

    // Extract the characters between the first four and the middle four.
    const beforeMiddle = address.substring(4, middleIndex - 2);
    // Extract the characters between the middle four and the last four.
    const afterMiddle = address.substring(middleIndex + 2, address.length - 4);

    // Return the formatted address with specific parts wrapped in <strong> tags.
    return (
      `<strong>${firstFour}</strong>` +
      `${beforeMiddle}` +
      `<strong>${middleFour}</strong>` +
      `${afterMiddle}<strong>${lastFour}</strong>`
    );
  }, [address]); // Dependency array for useMemo, recalculates if address changes.
}

export default useFormattedCryptoAddress;
